import type { ResolvedLineSettings } from "~/utils/allocationLineSettings"
import { unitsToOrderUpTo } from "~/utils/allocationRuleMaths"
import { computeStockLevels, type RuleParams, type StockLevels } from "~/utils/replenishmentRules"

export type ImpactLineSettings = Pick<
  ResolvedLineSettings,
  "excluded" | "minDisplayQty" | "maxQty" | "orderMultiple"
>

export type ImpactLine = {
  storeId: string
  rateOfSale: number
  storeStock: number
  leadTimeDays: number
  settings: ImpactLineSettings
}

export type RuleParamsAt = { params: RuleParams; reviewDays: number }

export type RuleImpact = {
  lines: number
  stores: number
  unitsNow: number
  unitsDraft: number
  delta: number
  moved: number
  belowDraft: number
}

/** A line's levels from the rule, the proposal sized as the run sizes it within the line's settings: 0 when excluded. */
export function lineStockLevels(line: ImpactLine, rule: RuleParamsAt): StockLevels {
  const { settings } = line
  const levels = computeStockLevels(rule.params, {
    ros: line.rateOfSale,
    leadTimeDays: line.leadTimeDays,
    position: line.storeStock,
    reviewDays: rule.reviewDays,
  })
  const proposed = settings.excluded
    ? 0
    : unitsToOrderUpTo(levels.optimal, line.storeStock, {
        mdq: settings.minDisplayQty,
        maxQty: settings.maxQty,
        orderMultiple: settings.orderMultiple,
        ros: line.rateOfSale,
        transitDays: line.leadTimeDays,
      }).quantity
  return { ...levels, proposed }
}

/** A staged rule's estimated effect on the lines it governs, under saved vs draft params. */
export function ruleImpact(
  lines: readonly ImpactLine[],
  saved: RuleParamsAt,
  draft: RuleParamsAt,
): RuleImpact {
  let unitsNow = 0
  let unitsDraft = 0
  let belowDraft = 0
  let moved = 0
  for (const line of lines) {
    const now = lineStockLevels(line, saved)
    const next = lineStockLevels(line, draft)
    unitsNow += now.proposed
    unitsDraft += next.proposed
    if (!line.settings.excluded && line.storeStock < next.min) belowDraft += 1
    if (now.proposed !== next.proposed) moved += 1
  }
  return {
    lines: lines.length,
    stores: new Set(lines.map((line) => line.storeId)).size,
    unitsNow,
    unitsDraft,
    delta: unitsDraft - unitsNow,
    moved,
    belowDraft,
  }
}
